/**
 * 模块: 竞品追踪主容器
 * 职责: 组装竞品追踪页面的各个组件
 * 限制: 文件<280行
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Select,
  Space,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import type { CompetitorASIN } from '../../types';
import { useCompetitorData } from './useCompetitorData';
import { CompetitorTable } from './CompetitorTable';
import { CompetitorDetailModal } from './CompetitorDetailModal';
import './CompetitorTracker.css';

const { Option } = Select;

export const CompetitorTracker: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    loading,
    sku,
    filteredCompetitors,
    searchTerm,
    setSearchTerm,
    threatFilter,
    setThreatFilter,
    threatStats,
    loadData,
  } = useCompetitorData(id);

  const [selectedCompetitor, setSelectedCompetitor] = useState<CompetitorASIN | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  const handleViewDetail = (competitor: CompetitorASIN) => {
    setSelectedCompetitor(competitor);
    setDetailModalVisible(true);
  };

  return (
    <div className="competitor-tracker-page">
      <div className="page-header">
        <div>
          <h1>竞品追踪</h1>
          {sku && <p className="sku-name">{sku.name} ({sku.asin})</p>}
        </div>
        <Button icon={<ReloadOutlined />} onClick={loadData}>
          刷新
        </Button>
      </div>

      <Row gutter={16} className="stats-row">
        <Col span={6}>
          <Card>
            <Statistic
              title="竞品总数"
              value={threatStats.high + threatStats.medium + threatStats.low}
              prefix={<InfoCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="高威胁竞品"
              value={threatStats.high}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="中威胁竞品"
              value={threatStats.medium}
              valueStyle={{ color: '#faad14' }}
              prefix={<InfoCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="低威胁竞品"
              value={threatStats.low}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card className="filter-card">
        <Space size="middle">
          <Input
            placeholder="搜索ASIN或品牌"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="威胁等级"
            value={threatFilter}
            onChange={setThreatFilter}
            style={{ width: 150 }}
          >
            <Option value="all">全部</Option>
            <Option value="high">高威胁</Option>
            <Option value="medium">中威胁</Option>
            <Option value="low">低威胁</Option>
          </Select>
          <span className="result-count">
            找到 {filteredCompetitors.length} 个竞品
          </span>
        </Space>
      </Card>

      <Card className="table-card">
        <CompetitorTable
          dataSource={filteredCompetitors}
          loading={loading}
          onViewDetail={handleViewDetail}
        />
      </Card>

      <CompetitorDetailModal
        visible={detailModalVisible}
        competitor={selectedCompetitor}
        onClose={() => setDetailModalVisible(false)}
      />
    </div>
  );
};

